// =============================================================================
// features/admin/feature-flags/OrganizationAxisInput.tsx — Eixo "organizações"
// da audience de uma flag (F32-S04).
//
// Decisão de design registrada no PR (doc 27 é multi-tenant futuro — hoje o
// backend NÃO expõe nenhum endpoint de listagem de organizações; o schema
// (apps/api/src/db/schema/organizations.ts) documenta "No MVP existe
// exatamente 1 organização"). Como este slot não pode tocar apps/api/**
// (fora de escopo do F32-S04), não há diretório para buscar por nome — o
// eixo aceita UUID colado/digitado, validado no cliente, com um atalho para
// usar a organização do admin logado (única fonte de UUID de org disponível
// no frontend hoje, via useAuth()).
// =============================================================================

import * as React from 'react';
import { z } from 'zod';

import { Button } from '../../../components/ui/Button';
import { useAuth } from '../../../lib/auth-store';
import { cn } from '../../../lib/cn';

import { RemovableChip } from './RemovableChip';

interface OrganizationAxisInputProps {
  value: string[];
  onChange: (organizationIds: string[]) => void;
  disabled?: boolean;
}

const UuidSchema = z.string().uuid();

export function OrganizationAxisInput({
  value,
  onChange,
  disabled = false,
}: OrganizationAxisInputProps): React.JSX.Element {
  const [draft, setDraft] = React.useState('');
  const [error, setError] = React.useState<string | null>(null);
  const { user } = useAuth();

  function commitDraft(): void {
    const trimmed = draft.trim();
    if (!trimmed) return;
    const result = UuidSchema.safeParse(trimmed);
    if (!result.success) {
      setError('UUID inválido. Copie o ID da organização, não o nome.');
      return;
    }
    if (value.includes(trimmed)) {
      setError('Essa organização já está na lista.');
      return;
    }
    onChange([...value, trimmed]);
    setDraft('');
    setError(null);
  }

  function remove(id: string): void {
    onChange(value.filter((v) => v !== id));
  }

  const myOrgId = user?.organizationId ?? null;
  const myOrgAlreadyAdded = myOrgId !== null && value.includes(myOrgId);

  return (
    <div className="flex flex-col gap-2">
      {value.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {value.map((id) => {
            const chipLabel = (
              <>
                <code className="font-mono normal-case tracking-normal">{id}</code>
                {id === myOrgId && <span className="ml-1">(minha)</span>}
              </>
            );
            return disabled ? (
              <span
                key={id}
                className="inline-flex items-center rounded-pill px-2.5 py-0.5 font-sans text-xs"
                style={{ background: 'var(--surface-muted)', color: 'var(--text-3)' }}
              >
                {chipLabel}
              </span>
            ) : (
              <RemovableChip
                key={id}
                ariaLabel={`Remover organização ${id}`}
                onRemove={() => remove(id)}
              >
                {chipLabel}
              </RemovableChip>
            );
          })}
        </div>
      )}

      {!disabled && (
        <>
          <div className="flex gap-2">
            <input
              type="text"
              value={draft}
              onChange={(e) => {
                setDraft(e.target.value);
                setError(null);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  commitDraft();
                }
              }}
              placeholder="Colar UUID da organização…"
              className={cn(
                'flex-1 px-3 py-2 rounded-sm text-sm font-mono',
                'border border-border-strong text-ink',
                'placeholder:font-sans placeholder:text-ink-4',
                'focus:outline-none focus:border-azul',
                'focus:shadow-[0_0_0_3px_rgba(27,58,140,0.15)]',
                'transition-[border-color,box-shadow] duration-fast ease',
                error && 'border-danger',
              )}
              style={{
                background: 'var(--bg-elev-1)',
                boxShadow: 'inset 0 1px 2px var(--border-inner-dark)',
              }}
            />
            <Button type="button" variant="outline" size="sm" onClick={commitDraft}>
              Adicionar
            </Button>
          </div>

          {myOrgId && !myOrgAlreadyAdded && (
            <button
              type="button"
              onClick={() => onChange([...value, myOrgId])}
              className="self-start font-sans text-xs text-azul underline hover:text-azul-deep"
            >
              Usar minha organização ({myOrgId.slice(0, 8)}…)
            </button>
          )}
        </>
      )}

      {error && <span className="text-xs text-danger">{error}</span>}
    </div>
  );
}
